/**
 * GroupResponsesPublicView — HDMI-vy som visar en grupps råsvar.
 * Facilitatorn väljer grupp via TV-knappen i presentationsläget.
 * Lyssnar på display.groupId + responses via onSnapshot.
 * Kräver ingen inloggning.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../../../firebase/config';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { useCompanyLogo } from '../../../hooks/useQRLogo';

export default function GroupResponsesPublicView({ sessionId }) {
  const [session,   setSession]   = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const companyLogo = useCompanyLogo();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      setSession(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsub;
  }, [sessionId]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'workshops', sessionId, 'responses'), snap => {
      setResponses(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
    });
    return unsub;
  }, [sessionId]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', height: '100%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: '#E05500' }} />
    </Box>
  );

  const groupId    = session?.display?.groupId;
  const groups     = session?.config?.groups     || [];
  const questions  = session?.config?.questions  || [];
  const currentQ   = session?.status?.currentQuestion ?? 0;
  const group      = groups.find(g => g.id === groupId);
  const groupColor = group?.color || '#E05500';

  if (!group) return (
    <Box sx={{ minHeight: '100vh', height: '100%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ color: '#333' }}>Väntar på grupp…</Typography>
    </Box>
  );

  // Filter responses for this group and current question
  const groupResponses = responses.filter(
    r => r.groupId === groupId && r.answers?.[currentQ] !== undefined && r.answers?.[currentQ] !== ''
  );

  const question = questions[currentQ];

  return (
    <Box sx={{
      minHeight: '100vh', height: '100%',
      bgcolor: '#1a1a1a',
      display: 'flex', flexDirection: 'column',
      px: { xs: 4, md: 10 }, py: { xs: 4, md: 7 },
      userSelect: 'none',
    }}>
      {/* Group header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2, borderBottom: `3px solid ${groupColor}` }}>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: groupColor, flexShrink: 0 }} />
        <Typography sx={{ color: '#fff', fontSize: 'clamp(1rem, 2.5vw, 1.8rem)', fontWeight: 700 }}>
          {group.name}
        </Typography>
        {question && (
          <Typography sx={{ color: '#555', fontSize: 'clamp(0.75rem, 1.5vw, 1.1rem)', ml: 1, flex: 1 }}>
            — {question.text}
          </Typography>
        )}
      </Box>

      {/* Answers */}
      {groupResponses.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: '#333', fontSize: '1rem' }}>Inga svar ännu…</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, flex: 1 }}>
          {groupResponses
            .sort((a, b) => {
              // Sort: ranked first (gold→silver→bronze), then by stars, then by name
              const rankOrder = { gold: 0, silver: 1, bronze: 2 };
              const aRank = a.answerRanks?.[currentQ];
              const bRank = b.answerRanks?.[currentQ];
              if (aRank && !bRank) return -1;
              if (!aRank && bRank) return 1;
              if (aRank && bRank) return (rankOrder[aRank] ?? 9) - (rankOrder[bRank] ?? 9);
              const aStars = (a.answerStars?.[currentQ] || []).length;
              const bStars = (b.answerStars?.[currentQ] || []).length;
              return bStars - aStars;
            })
            .map((r, i) => {
              const rank  = r.answerRanks?.[currentQ];
              const stars = (r.answerStars?.[currentQ] || []).length;
              const rankEmoji = rank === 'gold' ? '🥇' : rank === 'silver' ? '🥈' : rank === 'bronze' ? '🥉' : null;
              return (
                <Box key={r.docId} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 2.5 },
                  px: { xs: 2, md: 3 }, py: { xs: 1.25, md: 1.75 },
                  borderRadius: 1.5,
                  bgcolor: rank ? groupColor + '15' : 'rgba(255,255,255,0.03)',
                  border: '1px solid',
                  borderColor: rank ? groupColor + '44' : 'rgba(255,255,255,0.06)',
                  borderLeft: `3px solid ${rank ? groupColor : 'rgba(255,255,255,0.08)'}`,
                }}>
                  {rankEmoji ? (
                    <Typography sx={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)', flexShrink: 0, lineHeight: 1.4 }}>
                      {rankEmoji}
                    </Typography>
                  ) : stars > 0 ? (
                    <Typography sx={{ color: '#555', fontSize: 'clamp(0.7rem, 1.2vw, 0.9rem)', flexShrink: 0, mt: 0.25, fontWeight: 600 }}>
                      {'★'.repeat(Math.min(stars, 5))}
                    </Typography>
                  ) : (
                    <Typography sx={{ color: '#333', fontSize: 'clamp(0.65rem, 1vw, 0.8rem)', flexShrink: 0, mt: 0.25 }}>
                      {i + 1}.
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{
                      color: rank ? '#fff' : '#ccc',
                      fontSize: 'clamp(0.9rem, 2vw, 1.5rem)',
                      fontWeight: rank ? 600 : 400,
                      lineHeight: 1.4,
                    }}>
                      {r.answers[currentQ]}
                    </Typography>
                    <Typography sx={{ color: '#444', fontSize: 'clamp(0.6rem, 1vw, 0.75rem)', mt: 0.25 }}>
                      {r.displayName}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
        </Box>
      )}

      {/* Logo */}
      {companyLogo && (
        <Box component="img" src={companyLogo} alt="" sx={{
          position: 'fixed', bottom: { xs: 16, md: 28 }, right: { xs: 16, md: 32 },
          height: { xs: 22, md: 32 }, maxWidth: 140,
          objectFit: 'contain', opacity: 0.4,
        }} />
      )}
    </Box>
  );
}
